import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { CreateServiceDialog } from '../components/serviceOnboarding/CreateServiceDialog';
import { ServiceOnboardingCard } from '../components/serviceOnboarding/ServiceOnboardingCard';
import { ServiceListLabelFilter } from '../components/serviceOnboarding/ServiceListLabelFilter';
import { matchesConfigLabel } from '../lib/adapterCapabilityCatalog';
import type { AdapterServiceDto } from '../domain/adapterService';
import {
  useBootstrapAdapterServices,
  useAdapterServices,
  useDeleteAdapterService,
} from '../hooks/useAdapterServices';
import { messageFromAdminConsoleAxiosError } from '../lib/axiosErrorMessage';

export function ServiceOnboardingPage() {
  const navigate = useNavigate();
  const { data: items, isLoading, isError, error } = useAdapterServices();
  const bootstrap = useBootstrapAdapterServices();
  const deleteService = useDeleteAdapterService();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdapterServiceDto | null>(null);
  const [labelFilter, setLabelFilter] = useState('');

  const filteredItems = useMemo(
    () => (items ?? []).filter(svc => matchesConfigLabel(svc.configLabel, labelFilter)),
    [items, labelFilter],
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Service Onboarding
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Define third-party-adapter services, capabilities, and canonical request/response contracts (vendor-agnostic).
            Provider Onboarding maps vendors to these capabilities.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <ServiceListLabelFilter value={labelFilter} onChange={setLabelFilter} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Add service
          </Button>
          <Button variant="outlined" disabled={bootstrap.isPending} onClick={() => bootstrap.mutate()}>
            {bootstrap.isPending ? 'Importing…' : 'Import registry'}
          </Button>
        </Stack>
      </Stack>

      <CreateServiceDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleteService.isPending && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete service?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This removes <strong>{deleteTarget?.displayName}</strong> (
            <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {deleteTarget?.adapterServiceKey}
            </Box>
            , label <code>{deleteTarget?.configLabel ?? 'default'}</code>) and all capabilities. This cannot be
            undone.
          </Typography>
          {deleteService.isError ? (
            <Alert severity="error">{messageFromAdminConsoleAxiosError(deleteService.error)}</Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteService.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteService.isPending || !deleteTarget}
            onClick={() => {
              if (!deleteTarget) return;
              deleteService.mutate(deleteTarget.id, {
                onSuccess: () => {
                  setDeleteTarget(null);
                  navigate('/service-onboarding');
                },
              });
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {bootstrap.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Registry import: {bootstrap.data.created} created, {bootstrap.data.skipped} already present.
        </Alert>
      )}
      {bootstrap.isError && (
        <Alert severity="error">{messageFromAdminConsoleAxiosError(bootstrap.error)}</Alert>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && (
        <Alert severity="error">{messageFromAdminConsoleAxiosError(error)}</Alert>
      )}

      {!isLoading && !isError && filteredItems.length === 0 && (items?.length ?? 0) > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No services match adapter config label &quot;{labelFilter.trim()}&quot;. Choose another label or select All
          labels.
        </Alert>
      )}

      {!isLoading && !isError && filteredItems.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          {filteredItems.map(svc => (
            <ServiceOnboardingCard
              key={svc.id}
              service={svc}
              onDelete={setDeleteTarget}
            />
          ))}
        </Box>
      )}

      {!isLoading && items?.length === 0 && (
        <Alert
          severity="info"
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={() => setCreateOpen(true)}>
                Add service
              </Button>
              <Button color="inherit" size="small" onClick={() => bootstrap.mutate()}>
                Import registry
              </Button>
            </Stack>
          }
        >
          No services yet. Add a service manually or import the built-in registry (income-verification,
          identity-verification, ai-gateway).
        </Alert>
      )}
    </Box>
  );
}
